/**
 * DashboardOpenPanel — inline-панель «Открытый доступ» в строке квиза кабинета
 * `/me` (Фаза 10, И12/С4; план §C6.4, accordion-паттерн секции B без модалок).
 *
 * Зоны:
 * - Публикация: не опубликован → «Опубликовать» (409 limit_open_quizzes →
 *   подсказка «снимите другую публикацию»); опубликован → ссылка /q/<slug> +
 *   «Копировать» + QR (qrcode.react — тот же компонент, что PIN-экран, О12) +
 *   «Снять публикацию» (window.confirm — прецедент подтверждений проекта).
 * - Счётчик попыток attemptCount/attemptLimit (+closed-бейдж «набор завершён»).
 * - «Прохождения»: поимённый листинг (ник/дата/статус/счёт/«верных X из Y») с
 *   offset-пагинацией «Показать ещё», inline-раскрытие детальной попытки
 *   (что ответил игрок + verdict + reveal; кэш по attemptId, §B4.2), блок
 *   «По вопросам» (% правильных барами из /open/stats), «Сбросить историю»
 *   с confirm по числу удаляемых (О8/В4).
 *
 * Отдельная зона от live-истории Dashboard (решение О3: две независимые
 * секции; live-сессии И5–И6 не трогаем).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import {
  ApiError,
  fetchOpenAttemptDetail,
  fetchOpenAttempts,
  fetchOpenQuizStats,
  fetchOpenStatus,
  getGame,
  parseWireQuizPayload,
  publishOpenQuiz,
  resetOpenAttempts,
  unpublishOpenQuiz,
  type OpenAttemptDetail,
  type OpenAttemptItem,
  type OpenStatus,
  type OpenQuizStats,
} from '../api';
import { errorMessage } from '../util';

/** Размер страницы листинга попыток (серверный дефолт limit=20). */
const ATTEMPTS_PAGE_SIZE = 20;

export interface DashboardOpenPanelProps {
  gameId: number;
  /** Квота публикаций из GET /api/limits (openQuizzesPerUser) — бейдж «N / limit». */
  publishLimit: number | null;
  /** Число уже опубликованных квизов владельца (считает Dashboard по статусам). */
  publishedCount: number;
  /** Сообщает Dashboard об изменении published (обновить счётчик квоты). */
  onPublishedChange: (published: boolean) => void;
}

/** Строка ответа игрока в детальной попытке: что ответил — человекочитаемо. */
function attemptAnswerText(
  t: (key: string, opts?: Record<string, string | number>) => string,
  kind: string,
  answer: { option: number; payload?: Record<string, unknown> },
): string {
  const payload = answer.payload;
  switch (kind) {
    case 'choice':
    case 'poll':
      return t('dashboard.open.answer.option', { n: answer.option + 1 });
    case 'open':
      return typeof payload?.text === 'string' ? payload.text : '—';
    case 'word':
      return typeof payload?.word === 'string' ? payload.word : '—';
    case 'match': {
      const pairs = payload?.pairs;
      if (Array.isArray(pairs)) {
        return pairs
          .map((p) => {
            if (typeof p === 'object' && p !== null) {
              const o = p as Record<string, unknown>;
              if (typeof o.left === 'string' && typeof o.right === 'string') {
                return t('dashboard.open.answer.pair', { left: o.left, right: o.right });
              }
            }
            return '—';
          })
          .join('; ');
      }
      return '—';
    }
    case 'order': {
      const order = payload?.order;
      if (Array.isArray(order)) {
        return t('dashboard.open.answer.order', {
          order: order.map((i) => (typeof i === 'number' ? i + 1 : '?')).join(' → '),
        });
      }
      return '—';
    }
    default:
      return '—';
  }
}

/** Компактная reveal-строка детальной попытки (правильный ответ; как finish). */
function attemptRevealText(
  t: (key: string, opts?: Record<string, string | number>) => string,
  reveal: Record<string, unknown> | undefined,
): string | null {
  if (!reveal) return null;
  if (typeof reveal.correctOption === 'number') {
    return t('dashboard.open.reveal.option', { n: (reveal.correctOption as number) + 1 });
  }
  if (Array.isArray(reveal.accept)) {
    return t('dashboard.open.reveal.accept', { accept: reveal.accept.join(', ') });
  }
  if (Array.isArray(reveal.pairs)) {
    const rows = reveal.pairs
      .filter(
        (p): p is { left: string; right: string } =>
          typeof p === 'object' &&
          p !== null &&
          typeof (p as Record<string, unknown>).left === 'string' &&
          typeof (p as Record<string, unknown>).right === 'string',
      )
      .map((p) => t('dashboard.open.reveal.pair', { left: p.left, right: p.right }));
    if (rows.length > 0) return rows.join('; ');
  }
  if (Array.isArray(reveal.correctOrder)) {
    return t('dashboard.open.reveal.order', {
      order: reveal.correctOrder.map((i) => (typeof i === 'number' ? i + 1 : '?')).join(' → '),
    });
  }
  return null;
}

export function DashboardOpenPanel({
  gameId,
  publishLimit,
  publishedCount,
  onPublishedChange,
}: DashboardOpenPanelProps): JSX.Element {
  const { t } = useTranslation();

  const [status, setStatus] = useState<OpenStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  // Фаза 14 (эпик B4, §3.6 решение Д): «run не работает в открытых квизах» —
  // показывается после публикации квиза с run-вопросами (информационное, не
  // блокирует действие); testid editor-run-open-warn (единый с редактором).
  const [runWarn, setRunWarn] = useState(false);

  const [attempts, setAttempts] = useState<OpenAttemptItem[]>([]);
  const [attemptsTotal, setAttemptsTotal] = useState(0);
  const [attemptsLoading, setAttemptsLoading] = useState(true);
  const [attemptsError, setAttemptsError] = useState<string | null>(null);

  const [stats, setStats] = useState<OpenQuizStats | null>(null);

  // Inline-раскрытие деталей попытки (кэш, паттерн §B4.2).
  const [openDetailIds, setOpenDetailIds] = useState<Set<number>>(new Set());
  const [detailById, setDetailById] = useState<Record<number, OpenAttemptDetail>>({});
  const [detailPendingIds, setDetailPendingIds] = useState<Set<number>>(new Set());
  const [detailErrorById, setDetailErrorById] = useState<Record<number, string>>({});

  // Колбэк Dashboard стабилизируем через ref: инлайн-стрелка родителя меняет
  // identity на каждый его рендер — иначе refreshStatus перезапускался бы
  // бесконечно (onPublishedChange → setState родителя → новый колбэк → …).
  const onPublishedChangeRef = useRef(onPublishedChange);
  useEffect(() => {
    onPublishedChangeRef.current = onPublishedChange;
  });

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const s = await fetchOpenStatus(gameId);
      setStatus(s);
      onPublishedChangeRef.current(s.published);
    } catch (e) {
      setStatusError(errorMessage(e));
    } finally {
      setStatusLoading(false);
    }
  }, [gameId]);

  const refreshAttempts = useCallback(async () => {
    setAttemptsLoading(true);
    setAttemptsError(null);
    try {
      const resp = await fetchOpenAttempts(gameId, { limit: ATTEMPTS_PAGE_SIZE, offset: 0 });
      setAttempts(resp.items);
      setAttemptsTotal(resp.total);
    } catch (e) {
      setAttemptsError(errorMessage(e));
    } finally {
      setAttemptsLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    void refreshStatus();
    void refreshAttempts();
  }, [refreshStatus, refreshAttempts]);

  // Агрегаты «По вопросам» — non-blocking (ошибка не роняет панель).
  useEffect(() => {
    let cancelled = false;
    fetchOpenQuizStats(gameId)
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch(() => {
        // Non-blocking: блок просто не показывается.
      });
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  /**
   * Проверка наличия run-вопросов в публикуемом квизе (§3.6): панель не имеет
   * payload — грузим GET /api/games/{id} и ищем kind:'run' в wire-вопросах.
   * Ошибки проверки не роняют публикацию (предупреждение опционально).
   */
  async function checkRunWarn(): Promise<void> {
    try {
      const full = await getGame(gameId);
      const wire = parseWireQuizPayload(full.payload);
      setRunWarn(wire.questions.some((q) => q.kind === 'run'));
    } catch {
      setRunWarn(false);
    }
  }

  async function handlePublish(): Promise<void> {
    setPublishing(true);
    setActionError(null);
    setRunWarn(false);
    try {
      await publishOpenQuiz(gameId);
      setShowQr(false);
      await checkRunWarn();
      await refreshStatus();
      await refreshAttempts();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setActionError(t('dashboard.open.errors.limit'));
      } else {
        setActionError(errorMessage(e));
      }
    } finally {
      setPublishing(false);
    }
  }

  async function handleUnpublish(): Promise<void> {
    if (!window.confirm(t('dashboard.open.confirmUnpublish'))) return;
    setPublishing(true);
    setActionError(null);
    try {
      await unpublishOpenQuiz(gameId);
      setShowQr(false);
      await refreshStatus();
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setPublishing(false);
    }
  }

  async function handleCopy(): Promise<void> {
    if (!status?.url) return;
    try {
      await navigator.clipboard.writeText(status.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard недоступен (http/старый браузер) — ссылка остаётся
      // выделяемой <a>; пользователь копирует вручную.
      setCopied(false);
    }
  }

  async function handleMore(): Promise<void> {
    setAttemptsLoading(true);
    setAttemptsError(null);
    try {
      const resp = await fetchOpenAttempts(gameId, {
        limit: ATTEMPTS_PAGE_SIZE,
        offset: attempts.length,
      });
      setAttempts((prev) => [...prev, ...resp.items]);
      setAttemptsTotal(resp.total);
    } catch (e) {
      setAttemptsError(errorMessage(e));
    } finally {
      setAttemptsLoading(false);
    }
  }

  async function handleToggleDetail(item: OpenAttemptItem): Promise<void> {
    const open = openDetailIds.has(item.id);
    setOpenDetailIds((prev) => {
      const next = new Set(prev);
      if (open) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
    if (open || detailById[item.id] !== undefined || detailPendingIds.has(item.id)) return;
    setDetailPendingIds((prev) => new Set(prev).add(item.id));
    setDetailErrorById((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    try {
      const detail = await fetchOpenAttemptDetail(item.id);
      setDetailById((prev) => ({ ...prev, [item.id]: detail }));
    } catch (e) {
      setDetailErrorById((prev) => ({ ...prev, [item.id]: errorMessage(e) }));
    } finally {
      setDetailPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }

  async function handleReset(): Promise<void> {
    if (
      !window.confirm(
        t('dashboard.open.confirmReset', {
          n: attemptsTotal,
        }),
      )
    ) {
      return;
    }
    setPublishing(true);
    setActionError(null);
    try {
      await resetOpenAttempts(gameId);
      setOpenDetailIds(new Set());
      setDetailById({});
      await refreshStatus();
      await refreshAttempts();
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setPublishing(false);
    }
  }

  if (statusLoading) {
    return (
      <div className="open-panel" data-testid="me-open-panel">
        <p className="muted">{t('common.loading')}</p>
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="open-panel" data-testid="me-open-panel">
        <p className="error" role="alert" data-testid="me-open-status-error">
          {statusError}
        </p>
        <button type="button" className="link-btn" onClick={() => void refreshStatus()}>
          {t('common.refresh')}
        </button>
      </div>
    );
  }

  if (!status) return <div className="open-panel" data-testid="me-open-panel" />;

  return (
    <div className="open-panel" data-testid="me-open-panel">
      {/* Публикация (Ц1) */}
      {status.published && status.url ? (
        <div className="open-panel__row">
          <a
            className="open-panel__link"
            href={status.url}
            target="_blank"
            rel="noreferrer"
            data-testid="me-open-link"
          >
            {status.url}
          </a>
          <button
            type="button"
            className="link-btn"
            onClick={() => void handleCopy()}
            data-testid="me-open-copy"
          >
            {copied ? t('dashboard.open.copied') : t('dashboard.open.copy')}
          </button>
          <button
            type="button"
            className="link-btn"
            onClick={() => setShowQr((v) => !v)}
            aria-expanded={showQr}
            data-testid="me-open-qr-toggle"
          >
            {showQr ? t('dashboard.open.qrHide') : t('dashboard.open.qrShow')}
          </button>
          <button
            type="button"
            className="link-btn"
            onClick={() => void handleUnpublish()}
            disabled={publishing}
            data-testid="me-open-unpublish"
          >
            {t('dashboard.open.unpublish')}
          </button>
        </div>
      ) : (
        <div className="open-panel__row">
          {publishLimit !== null ? (
            <span className="muted" data-testid="me-open-quota">
              {t('dashboard.open.quota', { n: publishedCount, limit: publishLimit })}
            </span>
          ) : null}
          <button
            type="button"
            className="btn"
            onClick={() => void handlePublish()}
            disabled={publishing}
            data-testid="me-open-publish"
          >
            {publishing ? t('common.loading') : t('dashboard.open.publish')}
          </button>
        </div>
      )}
      {showQr && status.url ? (
        <div className="open-panel__qr" data-testid="me-open-qr">
          <div className="qr-wrap" aria-label={t('dashboard.open.qrAria', { url: status.url })}>
            <QRCodeSVG value={status.url} size={196} level="M" marginSize={4} />
          </div>
        </div>
      ) : null}
      {runWarn ? (
        <p className="editor-warn" role="status" data-testid="editor-run-open-warn">
          ⚠ {t('host.editor.runOpenWarn')}
        </p>
      ) : null}
      {actionError ? (
        <p className="error" role="alert" data-testid="me-open-action-error">
          {actionError}
        </p>
      ) : null}

      {/* Счётчик попыток (Ц5) */}
      <div className="open-panel__row">
        <span className="muted" data-testid="me-open-attempts-count">
          {t('dashboard.open.attemptsCount', {
            n: status.attemptCount,
            limit: status.attemptLimit,
          })}
        </span>
        {status.closed ? (
          <span className="pill" data-testid="me-open-closed">
            {t('dashboard.open.closedBadge')}
          </span>
        ) : null}
      </div>

      {/* Прохождения (Ц4) */}
      <h4 className="open-panel__section-title">{t('dashboard.open.attemptsTitle')}</h4>
      {attemptsLoading && attempts.length === 0 ? (
        <p className="muted" data-testid="me-open-attempts-loading">
          {t('common.loading')}
        </p>
      ) : attemptsError ? (
        <p className="error" role="alert" data-testid="me-open-attempts-error">
          {attemptsError}
        </p>
      ) : attempts.length === 0 ? (
        <p className="muted" data-testid="me-open-attempts-empty">
          {t('dashboard.open.attemptsEmpty')}
        </p>
      ) : (
        <>
          <ul className="open-attempts" data-testid="me-open-attempts-list">
            {attempts.map((item) => {
              const open = openDetailIds.has(item.id);
              const detail = detailById[item.id];
              const detailError = detailErrorById[item.id];
              return (
                <li key={item.id} className="open-attempts__row" data-testid="me-open-attempt-row">
                  <span className="open-attempts__nick" data-testid="me-open-attempt-nick">
                    {item.nickname}
                  </span>
                  <span className="muted" data-testid="me-open-attempt-date">
                    {new Date(item.startedAt).toLocaleString()}
                  </span>
                  <span className="muted">
                    {t(`dashboard.open.status.${item.status}`, { defaultValue: item.status })}
                  </span>
                  <span className="muted" data-testid="me-open-attempt-score">
                    {t('dashboard.open.attemptSummary', {
                      score: item.score,
                      correct: item.correctCount,
                      total: item.answerCount,
                    })}
                  </span>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => void handleToggleDetail(item)}
                    data-testid="me-open-attempt-detail"
                  >
                    {open ? t('dashboard.open.hide') : t('dashboard.open.detail')}
                  </button>
                  {open ? (
                    <div className="open-attempts__detail" data-testid="me-open-attempt-detail-box">
                      {detailError ? (
                        <p className="error" role="alert">
                          {detailError}
                        </p>
                      ) : detail ? (
                        detail.answers.map((a) => (
                          <p key={a.questionId} className="open-finish__mine">
                            <strong>{a.questionId}.</strong> {attemptAnswerText(t, a.kind, a)} ·{' '}
                            <span className={a.correct ? 'q-hint--ok' : 'q-hint--wrong'}>
                              {a.correct ? `✓ +${a.points}` : `✗ +${a.points}`}
                            </span>
                            {attemptRevealText(t, a.reveal) ? (
                              <> · {attemptRevealText(t, a.reveal)}</>
                            ) : null}
                          </p>
                        ))
                      ) : (
                        <p className="muted">{t('common.loading')}</p>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {attempts.length < attemptsTotal ? (
            <button
              type="button"
              className="link-btn"
              onClick={() => void handleMore()}
              disabled={attemptsLoading}
              data-testid="me-open-attempts-more"
            >
              {t('dashboard.history.showMore')}
            </button>
          ) : null}
        </>
      )}

      {/* По вопросам (Ц4-агрегаты) */}
      {stats && stats.questions.length > 0 ? (
        <>
          <h4 className="open-panel__section-title">{t('dashboard.open.statsTitle')}</h4>
          <div data-testid="me-open-stats">
            {stats.questions.map((q) => {
              const pct = q.total > 0 ? Math.round((q.correct / q.total) * 100) : 0;
              return (
                <div key={q.questionId} className="open-panel__row">
                  <span className="muted" style={{ minWidth: '3.5rem' }}>
                    {t('dashboard.open.statsQuestion', { n: q.questionId })}
                  </span>
                  <span className="open-stats__bar" aria-hidden="true">
                    <span className="open-stats__fill" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="muted" data-testid={`me-open-stat-${q.questionId}`}>
                    {t('dashboard.open.statsValue', { pct, correct: q.correct, total: q.total })}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {/* Сброс истории (Ц5) */}
      <div className="open-panel__row">
        <button
          type="button"
          className="link-btn"
          onClick={() => void handleReset()}
          disabled={publishing || attemptsTotal === 0}
          data-testid="me-open-reset"
        >
          {t('dashboard.open.reset')}
        </button>
      </div>
    </div>
  );
}
